import {
  type GraphComponent,
  type ILabel,
  type ILabelStyle,
  INode,
  type IOrientedRectangle,
  type IRenderContext,
  LabelStyleBase,
  type Matrix,
  type Size,
  SvgVisual,
  SvgVisualGroup,
  type TaggedSvgVisual
} from '@yfiles/yfiles';
import {
  calculateHeightVector,
  getNodeElevation,
  getNodeHeight,
  getVisualTranslation
} from './Pseudo3DNodeStyle';
import { getOrientation } from './util.ts';

type MyVisual = TaggedSvgVisual<SVGGElement, RenderDataCache>;

class RenderDataCache {
  public layout: IOrientedRectangle;
  public nodeHeight: number;
  public nodeElevation: number;
  public projection: Matrix;

  constructor(
    layout: IOrientedRectangle,
    nodeHeight: number,
    nodeElevation: number,
    projection: Matrix
  ) {
    this.layout = layout;
    this.nodeHeight = nodeHeight;
    this.nodeElevation = nodeElevation;
    this.projection = projection;
  }
}

export class Pseudo3DLabelStyle extends LabelStyleBase<MyVisual> {
  public readonly wrappedLabelStyle: ILabelStyle;

  constructor(wrappedLabelStyle: ILabelStyle) {
    super();
    this.wrappedLabelStyle = wrappedLabelStyle;
  }

  protected createVisual(context: IRenderContext, label: ILabel): MyVisual | null {
    if (!(label.owner instanceof INode)) {
      throw new Error('Label owner must be an INode');
    }

    const orientation = getOrientation(context.canvasComponent as GraphComponent);

    const node = label.owner;
    const nodeHeight = getNodeHeight(node);
    const nodeElevation = getNodeElevation(context, node);

    const translationLength = getVisualTranslation(nodeHeight + nodeElevation, orientation.inclination);
    const heightVector = calculateHeightVector(context.projection);
    const translation = heightVector.multiply(translationLength);

    const labelVisual = this.wrappedLabelStyle.renderer
      .getVisualCreator(label, this.wrappedLabelStyle)
      .createVisual(context) as SvgVisual;

    const group = new SvgVisualGroup();
    SvgVisual.setTranslate(group.svgElement, translation.x, translation.y);
    group.add(labelVisual);

    return SvgVisual.from(
      group.svgElement as SVGGElement,
      new RenderDataCache(label.layout, nodeHeight, nodeElevation, context.projection)
    );
  }

  protected updateVisual(
    context: IRenderContext,
    oldVisual: MyVisual,
    label: ILabel
  ): MyVisual | null {
    if (!(label.owner instanceof INode)) {
      throw new Error('Label owner must be an INode');
    }
    const oldCache = oldVisual.tag;

    const node = label.owner;
    const nodeHeight = getNodeHeight(node);
    const nodeElevation = getNodeElevation(context, node);
    const newCache = new RenderDataCache(
      label.layout,
      nodeHeight,
      nodeElevation,
      context.projection
    );

    if (
      newCache.layout == oldCache.layout &&
      newCache.nodeHeight === oldCache.nodeHeight &&
      newCache.nodeElevation === oldCache.nodeElevation &&
      newCache.projection.hasSameValue(oldCache.projection)
    ) {
      return oldVisual;
    }

    return this.createVisual(context, label);
  }

  protected getPreferredSize(label: ILabel): Size {
    return this.wrappedLabelStyle.renderer.getPreferredSize(label, this.wrappedLabelStyle);
  }
}
